<template>
    <v-row no-gutters>
        <v-col cols="12" lg="8">
            <v-data-table
                :headers="salesChannelsHeaders"
                :items="channels"
                disable-sort
                density="compact"
                class="elevation-1 mb-2"
                :items-per-page="-1"
                hide-default-footer
            >
                <template v-slot:item.key="{item}">
                    {{ $t(`salesChannelsType.${item.key}`) }}
                </template>
                <template v-slot:item.value="{item}">
                    {{ salesChannelsNames(item.value) }}
                </template>
                <template v-slot:item.action="{item}">
                    <v-btn variant="text" class="text-caption" density="compact" @click="deleteSalesChannel(item.key)">
                        {{ $t('delete') }}
                    </v-btn>
                </template>
            </v-data-table>
            <v-btn data-cy="show-modal" @click="showAddSalesChannelModal">
                {{ $t('add') }}
            </v-btn>
        </v-col>
    </v-row>
    <ModalWrapper v-model="isShowModal" scrollable>
        <template v-slot:title>{{ $t('add_sales_channel') }}</template>
        <template v-slot:content>
            <v-select
                v-model="selectedSalesChannel"
                :items="availableSalesChannelsItems"
                :label="$t('sales_channel_name')"
                @update:model-value="loadSalesChannelItems"
            />
            <v-data-table
                v-if="selectedSalesChannel !== 'DIRECT_SALES'"
                v-model="selectedSalesChannelsItems"
                item-value="id"
                item-text="name"
                show-select
                return-object
                :headers="headers"
                :items="salesChannelsItems"
                density="compact"
                class="elevation-1"
                :items-per-page="-1"
                hide-default-footer
                :loading="supplierStore.isLoadingSalesChannels"
            >
                <template v-slot:item.active="{item}">
                    {{ item.active ? $t('active') : $t('inactive') }}
                </template>
            </v-data-table>
        </template>
        <template v-slot:btns>
            <v-btn variant="flat" data-cy="add-sales-channel" @click="addSalesChannel">
                {{ $t('add') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>
<script setup lang="ts">
    const {availableSalesChannels = ['AGENCY', 'CORPORATOR', 'TOUR_OPERATOR', 'DIRECT_SALES', 'B2C']} = defineProps<{
        availableSalesChannels?: string[]
    }>()

    const salesChannels = defineModel<any>()

    const emit = defineEmits<{
        (e: 'updateDirectSales', value: boolean): void
    }>()

    const isShowModal = ref<boolean>(false)

    const selectedSalesChannel = ref<string>('AGENCY')
    const selectedSalesChannelsItems = ref<any>([])

    const {t} = useI18n()

    const headers = computed(() => {
        return [
            {title: t('name'), value: 'name', sortable: true},
            {title: t('active'), value: 'active', sortable: true},
        ]
    })

    const salesChannelsHeaders = computed(() => {
        return [
            {title: t('sales_channel_name'), value: 'key', minWidth: '120px'},
            {title: t('name'), value: 'value'},
            {title: '', value: 'action', minWidth: '120px', width: '10%', align: 'end'},
        ]
    })

    const channels = computed(() => {
        return Object.entries(salesChannels.value).map(([key, value]) => ({key, value}))
    })

    const availableSalesChannelsItems = computed(() => {
        return availableSalesChannels.map(value => ({value, title: t(`salesChannelsType.${value}`)}))
    })

    const salesChannelsItems = computed(() => {
        switch (selectedSalesChannel.value) {
            case 'AGENCY':
                return supplierStore.agenciesList
            case 'CORPORATOR':
                return supplierStore.corpList
            case 'TOUR_OPERATOR':
                return supplierStore.TOList
            case 'DIRECT_SALES':
                return []
            case 'B2C':
                return supplierStore.b2sList
        }
        return []
    })

    const loadSalesChannelItems = async () => {
        switch (selectedSalesChannel.value) {
            case 'AGENCY':
                if (!supplierStore.agenciesLoaded) await supplierStore.loadCompaniesByType('TOUR_AGENCY')
                break
            case 'CORPORATOR':
                if (!supplierStore.corpLoaded) await supplierStore.loadCompaniesByType('CORPORATOR')
                break
            case 'TOUR_OPERATOR':
                if (!supplierStore.TOLoaded) await supplierStore.loadCompaniesByType('TOUR_OPERATOR')
                break
            case 'DIRECT_SALES':
                break
            case 'B2C':
                if (!supplierStore.b2sListLoaded) await supplierStore.loadB2C()
                break
        }
        selectedSalesChannelsItems.value =
            salesChannels.value[selectedSalesChannel.value]?.map(({name, agencyId}) => ({
                id: agencyId,
                name,
                active: salesChannelsItems.value.find(channel => channel.id === agencyId).active,
            })) || []
    }

    const salesChannelsNames = items => {
        return items.map(({name}) => name).join(', ')
    }

    const addSalesChannel = () => {
        if (selectedSalesChannel.value === 'DIRECT_SALES') emit('updateDirectSales', true)

        salesChannels.value[selectedSalesChannel.value] = selectedSalesChannelsItems.value?.map(({id, name}) => ({
            agencyId: id,
            name,
        }))
        isShowModal.value = false
    }

    const deleteSalesChannel = (salesChannelKey: string) => {
        if (salesChannelKey === 'DIRECT_SALES') emit('updateDirectSales', false)

        const {[salesChannelKey]: _, ...updatedSalesChannels} = salesChannels.value
        salesChannels.value = updatedSalesChannels
    }

    const showAddSalesChannelModal = async () => {
        isShowModal.value = true
        selectedSalesChannel.value = 'AGENCY'
        await loadSalesChannelItems()
    }
</script>
